import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { Activity } from '@spike/activity-model';
import Button from 'components/UI/V3/Button/Button';
import React from 'react';
import { useReportBuilder } from '../ReportBuilderProvider';
import { RecentActivityRow } from './RecentActivityRow';
import styles from './RecentActivitiesPanel.module.scss';

const isSameActivity = (a: Activity, b: Activity): boolean => a.uuid === b.uuid || (a.id > 0 && a.id === b.id);

const groupByDate = (activities: Activity[]): Array<{ dateKey: string; label: string; items: Activity[] }> => {
    const sorted = [...activities].sort((a, b) => b.date.valueOf() - a.date.valueOf());
    const map = new Map<string, Activity[]>();

    for (const activity of sorted) {
        const key = activity.date.format('YYYY-MM-DD');
        const group = map.get(key);
        if (group) {
            group.push(activity);
        } else {
            map.set(key, [activity]);
        }
    }

    return Array.from(map.entries()).map(([dateKey, items]) => ({
        dateKey,
        label: items[0].date.format('dddd, DD MMM YYYY'),
        items
    }));
};

interface RecentActivitiesPanelProps {
    petName: string;
    onAddActivity: () => void;
}

const RecentActivitiesPanelComponent: React.FC<RecentActivitiesPanelProps> = ({ petName, onAddActivity }) => {
    const {
        activities: reportActivities,
        recentActivities,
        isRecentActivitiesLoading,
        hasMoreRecentActivities,
        loadMoreRecentActivities,
        addExistingActivity,
        removeExistingActivityFromReport
    } = useReportBuilder();

    const groups = React.useMemo(() => groupByDate(recentActivities), [recentActivities]);

    const isInReport = React.useCallback(
        (activity: Activity): boolean => reportActivities.some(a => isSameActivity(a, activity)),
        [reportActivities]
    );

    if (recentActivities.length === 0 && !isRecentActivitiesLoading) {
        return (
            <div className={styles.emptyState}>
                <p className={styles.emptyTitle}>No Activities Found</p>
                <p className={styles.emptyMessage}>We couldn’t find any activities for {petName}.</p>
                <Button
                    label="Add Activity"
                    size="small"
                    leftIcon={faPlus}
                    className={styles.emptyAddButton}
                    onClick={onAddActivity}
                />
            </div>
        );
    }

    return (
        <div className={styles.root}>
            {groups.map(group => (
                <div
                    key={group.dateKey}
                    className={styles.group}
                >
                    <p className={styles.groupLabel}>{group.label}</p>
                    <div className={styles.items}>
                        {group.items.map(activity => (
                            <RecentActivityRow
                                key={activity.uuid}
                                activity={activity}
                                added={isInReport(activity)}
                                onAdd={addExistingActivity}
                                onRemove={removeExistingActivityFromReport}
                            />
                        ))}
                    </div>
                </div>
            ))}

            {hasMoreRecentActivities && (
                <Button
                    label="Load More"
                    variant="outlined"
                    size="medium"
                    fullWidth
                    isLoading={isRecentActivitiesLoading}
                    onClick={loadMoreRecentActivities}
                />
            )}
        </div>
    );
};

export const RecentActivitiesPanel = React.memo(RecentActivitiesPanelComponent);
